import { useState, useEffect, useRef, useMemo, createRef, useCallback, createContext, useContext } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { DonutIcon, HomeIcon, LearnIcon, MoonIcon, PlusCircle, SunIcon, ValidIcon } from "../components/icons";
import { useTheme } from "next-themes";
import Select, { components } from "react-select";

import RequestIcon from "./../assets/requests_page_icon.png";

// included in App, now app no more
// just default home layout
import "./../App.css";
import { useAuth } from "../store/AuthContext";
import toast from "react-hot-toast";
import { customToast } from "../components/toasts";
import { numberWithCommas } from "../utils/numbers";
import { getFundsByAmcCode } from "../services/funds.service";
import { getFAQs, getGlossary } from "../services/learn.service";
import { slugify } from "../utils/strings";
import { useI18n } from "../hooks/useI18n";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../config/languages";

import InvestorAppLogo from '../assets/InvestorApp.png';
import FundDetailsModal from "../components/modals/fundDetails";
import { getUnitholderDetails } from "../services/auth.service";
import { useAmc } from "../store/AmcContext";
import { notificationAPI } from "../utils/notification";

function CustomOption(props) {
  const { data, innerProps, isFocused } = props;

  const { theme } = useTheme();

  let content;
  switch (data.search_type) {
    case "fund":
      content = (
        <div className='d-flex align-items-center'>
          <div>
            <div
              className=''
              style={{
                color: theme === "dark" ? "var(--theme-light-secondary)" : "rgb(48, 116, 199)",
                fontWeight: "500",
                fontSize: "1.15rem",
                lineHeight: "1.1",
                letterSpacing: ".02em",
              }}
            >
              {data.fund_name}
            </div>
            <div
              style={{
                color: theme === "dark" ? "rgb(150, 150, 150)" : "rgb(100, 100, 100)",
                fontWeight: "400",
                fontSize: ".94rem",
                lineHeight: "1.1",
                letterSpacing: ".02em",
              }}
            >
              {data.symbol_code}
            </div>
          </div>
        </div>
      );
      break;
    case "glossary":
      content = (
        <div>
          <strong
            style={{
              color: theme === "dark" ? "var(--theme-light-secondary)" : "rgb(48, 116, 199)",
              fontWeight: "500",
              fontSize: "1.15rem",
              lineHeight: "1.1",
              letterSpacing: ".02em",
            }}
          >
            {data.title}
          </strong>
          <div
            style={{
              color: theme === "dark" ? "rgb(150, 150, 150)" : "rgb(100, 100, 100)",
              fontWeight: "400",
              fontSize: ".94rem",
              lineHeight: "1.1",
              letterSpacing: ".02em",
            }}
          >
            {"Glossary"}
          </div>
        </div>
      );
      break;
    case "faq":
      content = (
        <div>
          <div
            style={{
              display: "-webkit-box",
              boxOrient: "vertical",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: "2",
              overflow: "hidden",
              color: theme === "dark" ? "var(--theme-light-secondary)" : "rgb(48, 116, 199)",
              fontWeight: "500",
              fontSize: "1.15rem",
              lineHeight: "1.1",
              letterSpacing: ".02em",
            }}
          >
            {data.question}
          </div>
          <div
            style={{
              color: theme === "dark" ? "rgb(150, 150, 150)" : "rgb(100, 100, 100)",
              fontWeight: "400",
              fontSize: ".94rem",
              lineHeight: "1.1",
              letterSpacing: ".02em",
            }}
          >
            {"FAQ"}
          </div>
        </div>
      );
      break;
    default:
      content = <span>{"default case"}</span>;
  }
  return (
    <components.Option
      {...props}
      innerProps={innerProps}
      style={
        {
          // backgroundColor: isFocused ? 'rgba(0,123,255,0.1)' : 'transparent',
          // padding: '8px 12px',
        }
      }
    >
      {content}
    </components.Option>
  );
}

const NotificationContext = createContext()

export const useNotification = () => useContext(NotificationContext)

const HomeLayout = () => {
  const { pathname } = useLocation();

  const [screenSize, setScreenSize] = useState();
  const [searchFocus, setSearchFocus] = useState(false);
  const [tabScrollingLeft, setTabScrollingLeft] = useState(false);
  const [tabScrollingRight, setTabScrollingRight] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState(() => getMenuItemFromPath(pathname));
  const [searchInput, setSearchInput] = useState("");
  const [searchData, setSearchData] = useState([]);
  const [investInput, setInvestInput] = useState("250000");
  const [unitholderData, setUnitholderData] = useState(null);
  const [loading, setLoading] = useState(() => ({
    notifications: false,
    funds: true
  }));
  const [showFundsDetail, setShowFundsDetail] = useState(() => ({
    show: false,
    symbol: ""
  }))

  const [fundsData, setFundsData] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState(0);

  const { currentLanguageConfig, changeLanguage, isRTL } = useI18n();
  const { t } = useTranslation();

  const [isPortfolioModalOpen, setIsPortfolioModalOpen] = useState(false);

  const [addPortfolioData, setAddPortfolioData] = useState(() => ({
    name: "",
    description: "",
    funds: [],
  }));
  const { amcCode, loading: loadingAmcCode, error: amcCodeError } = useAmc();

  const tabsScrollRef = useRef(null);
  const searchInputRef = useRef(null);

  const notificationsRef = useRef(null);

  const { theme, setTheme } = useTheme();

  const { user, logout } = useAuth();

  const navigate = useNavigate();

  function getMenuItemFromPath(path) {
    const firstIndex = path.indexOf("/");
    const secondIndex = path.indexOf("/", firstIndex + 1);

    if (path === "/") return "dashboard";
    if (firstIndex === -1) return "dashboard";
    if (secondIndex === -1) return path.substring(firstIndex + 1);
    return path.substring(firstIndex + 1, secondIndex);
  }

  const handlePortfolioModalToggle = () => {
    if (activeMenuItem === "portfolio") {
      setIsPortfolioModalOpen(true);
    }
  };

  const updateFundsList = async () => {
    try {
      setLoading((prev) => ({ ...prev, funds: true }))
      const response = await getFundsByAmcCode(user);

      if (!response.error) {
        setFundsData(response.data);
      }

      // setLoading((prev) => ({ ...prev, notifications: true }));

      // await new Promise((res) => setTimeout(() => res(), 3000));
    } finally {

      setLoading((prev) => ({ ...prev, funds: false }))
      // setLoading((prev) => ({ ...prev, notifications: false }));
    }
  };

  /**
   * 	Checks the current active route
   *  to generate the relative tab buttons
   */

  const handleLeftScroll = () => {
    tabsScrollRef.current.scrollLeft -= 60;
  };

  const handleRightScroll = () => {
    tabsScrollRef.current.scrollLeft += 60;
  };

  const handleLogout = async () => {
    logout();

    toast((ts) => customToast(t("app.messages.logout.success"), "success", ts), {
      duration: 3000,
      removeDelay: 1000,
      style: {
        padding: "0.75rem",
        margin: "0",
        minWidth: "240px",
        backgroundColor: "rgb(255,255,255)",
      },
    });
  };

  const currentActiveTab = () => {
    const firstIndex = pathname.indexOf("/");

    if (pathname === "/") return activeMenuItem;

    if (firstIndex == -1) return activeMenuItem;

    if (pathname.split("/").length > 3) {
      const secondIndex = pathname.indexOf("/", firstIndex + 1);
      const thirdIndex = pathname.indexOf("/", secondIndex + 1);

      return pathname.slice(firstIndex, thirdIndex);
    } else {
      return pathname.slice(firstIndex + 1, pathname.length);
    }
  };

  const tabsData = useMemo(
    () => ({
      dashboard: [
        // {
        //   name: "Dashboard",
        //   path: "dashboard",
        // },
      ],
      invest: [
        // {
        // 	name: 'All Funds',
        // 	path: 'invest'
        // },
        // {
        // 	name: 'Shariah Funds',
        // 	path: 'invest/shariah'
        // },
        // {
        // 	name: 'Conventional Funds',
        // 	path: 'invest/conventional'
        // }
      ],
      portfolio: [
        // {
        // 	name: 'All',
        // 	path: 'portfolio'
        // }
      ],
      goals: [
        // {
        // 	name: 'Home',
        // 	path: 'goals'
        // },
      ],
      learn: [
        // {
        //   name: "Articles",
        //   path: "learn/articles",
        // },
        // {
        //   name: "Glossary",
        //   path: "learn/glossary",
        // },
        // {
        //   name: "FAQs",
        //   path: "learn/faqs",
        // },
      ],
      request: [],
    }),
    [activeMenuItem],
  );

  const updateSearchData = async () => {
    try {
      const tasks = [
        { fn: getFundsByAmcCode, meta: { search_type: "fund", redirection: "/invest" } },
        { fn: getGlossary, meta: { search_type: "glossary", redirection: "/learn/glossary" } },
        { fn: getFAQs, meta: { search_type: "faq", redirection: "/learn/faqs" } },
      ];

      const settled = await Promise.allSettled(tasks.map((task) => (task.meta.search_type === "fund" ? task.fn(user) : task.fn())));

      const merged = settled.flatMap((outcome, i) => {
        const { meta } = tasks[i];

        if (outcome.status !== "fulfilled") {
          // console.error(`Task ${i} rejected outright:`, outcome.reason);
          return [];
        }

        const result = outcome.value;

        if (result.error) {
          // console.warn(`API error on ${meta.route}:`, result.message);
          return [];
        }

        return result.data.map((item) => ({
          value: meta.search_type === "fund" ? item.symbol_code : meta.search_type === "glossary" ? item.title : item.question,
          label: meta.search_type === "fund" ? item.fund_name : item._id,
          ...item,
          ...meta,
        }));
      });

      setSearchData(merged);
    } catch (err) {
      // console.log(err);
    }
  };

  const updateNotifications = useCallback((notificationData, unreadCount) => {
    setNotifications(notificationData)
    setUnreadNotificationsCount(unreadCount)
  }, [])

  const notificationContextData = useMemo(() => ({
    notifications,
    updateNotifications
  }), [notifications, updateNotifications])

  useEffect(() => {
    // if (true) navigate('/auth')

    // if (!user) navigate('/auth');

    // getFunds(updateFunds);

    if (user && user?.data?.folio_no) {

      const fetchUnitholderDetails = async () => {
        try {
          const res = await getUnitholderDetails(user.data.email, user.data.folio_no);

          if (!res.error) {
            setUnitholderData(res.data[0]);
          }
        } catch (err) {
          // console.log(err)
        }
      }

      fetchUnitholderDetails()
    }

    updateFundsList();
    updateSearchData();

    const tabsRowScrollHandler = () => {
      if (tabsScrollRef.current.scrollLeft > 0) {
        setTabScrollingLeft(true);
      } else {
        setTabScrollingLeft(false);
      }

      if (Math.ceil(tabsScrollRef.current.clientWidth + tabsScrollRef.current.scrollLeft) >= tabsScrollRef.current.scrollWidth) {
        setTabScrollingRight(false);
      } else {
        setTabScrollingRight(true);
      }
    };

    const resizeScreen = () => {
      setScreenSize(window?.innerWidth);
      if (tabsScrollRef.current) {
        tabsRowScrollHandler();
      }
    };

    resizeScreen();
    if (tabsScrollRef.current) {
      tabsRowScrollHandler();
    }

    window.addEventListener("resize", resizeScreen);
    tabsScrollRef.current?.addEventListener("scroll", tabsRowScrollHandler);

    return () => {
      window.removeEventListener("resize", resizeScreen);
      tabsScrollRef.current?.removeEventListener("scroll", tabsRowScrollHandler);
    };
  }, []);

  useEffect(() => {
    if (!user || !user?.data || !user?.data?.folio_no) return;

    if (notificationsRef.current) return;

    notificationsRef.current = notificationAPI.startPolling(
      (newNotifications, count) => {
        setNotifications(() => [...newNotifications])
        setUnreadNotificationsCount(count);
      },
      5 * 60 * 1000,
      { folio_no: user.data.folio_no }
    )

    return () => {
      if (notificationsRef.current) {
        clearInterval(notificationsRef.current)
      }
    }
  }, [user])

  // useEffect(() => {
  //   // matches /dashboard and any nested routes like /dashboard/reports
  //   const isDashboardActive = matchPath({ path: '/dashboard/*', end: false }, location.pathname);
  //   setDashboardOpen(Boolean(isDashboardActive));
  // }, [location.pathname]);
  
  return (
    <div className='container-fluid p-0'>
      <div
        className='row w-100 mx-auto flex-column gap-sm-2 flex-nowrap'
        style={{
          height: "100dvh",
          // overflow: "hidden",
          padding: "8px",
          background: "linear-gradient(90deg, var(--theme-primary) 0%, var(--theme-secondary) 100%)",
        }}
      >
        {/* HEADER */}
        <div
          className='col-12 flex-grow-0 flex-shrink-0'
          style={{
            paddingTop: "4px",
            paddingBottom: "6px",
            paddingInline: screenSize >= 576 ? "10px" : "7px",
          }}
        >
          <div
            className='row w-100 mx-auto align-items-center gap-sm-4 gap-lg-0 flex-nowrap'
            style={
              {
                // minHeight: "63.79px",
              }
            }
          >
            <div className='col-auto col-lg p-0'>
              <Link
                  onClick={() => setActiveMenuItem("dashboard")}
                  to='dashboard'
                  style={{
                    textDecoration: "none",
                  }}>
                    <img className="" src={theme === 'dark' ? InvestorAppLogo : InvestorAppLogo} style={{
                      width: '140px',
                      maxWidth: '100%'
                    }}/>
                  </Link>
              {/* <div className='d-flex align-items-center justify-content-start'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width={32}
                  height={32}
                  viewBox='0 0 24 24'
                  fill='none'
                  stroke='currentColor'
                  strokeWidth={2}
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  className='icon icon-tabler icons-tabler-outline icon-tabler-stack-2'
                  style={{
                    color: "white",
                  }}
                >
                  <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                  <path d='M12 4l-8 4l8 4l8 -4l-8 -4' />
                  <path d='M4 12l8 4l8 -4' />
                  <path d='M4 16l8 4l8 -4' />
                </svg>

                <div
                  className=''
                  style={{
                    fontSize: "1.6rem",
                    fontFamily: "Kanit",
                    fontWeight: "600",
                    letterSpacing: "0.05em",
                    color: "white",
                    marginLeft: "8px",
                  }}
                >
                  OneApp
                </div>
              </div> */}
            </div>

            {/* MENU BAR -- DESKTOP */}
            <div className='d-none d-lg-block col-auto p-0'>
              <div
                className='row w-100 mx-auto justify-content-between justify-content-sm-center flex-nowrap'
                style={{
                  paddingBottom: "4px",
                  paddingTop: "4px",
                  paddingInline: screenSize >= 1200 ? "3rem" : screenSize <= 575 ? ".5rem" : "1.5rem",
                  gap: screenSize <= 575 ? "" : "2.5rem",
                }}
              >
                <Link
                  className='col-auto p-0'
                  onClick={() => setActiveMenuItem("dashboard")}
                  to='dashboard'
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <div className='d-flex flex-column align-items-center justify-content-start'>
                    {activeMenuItem === "dashboard" ? (
                      <>
                        <div
                          className='position-relative d-flex align-items-center justify-content-center'
                          style={{
                            width: "36px",
                            height: "36px",
                          }}
                        >
                          <div
                            className='w-100 h-100 d-flex align-items-center justify-content-center'
                            style={{
                              backgroundColor: "rgb(203, 108, 230)",
                              borderRadius: "50%",
                              // top: '0',
                              margin: "0 auto",
                              paddingBottom: "1px",
                              // transform: 'translate(-2%, -8%)'
                            }}
                          >
                            <HomeIcon width={30} height={30} color='white' />
                          </div>
                        </div>
                        {/* <DonutIcon width={36} height={36} color='white' /> */}
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          {t("app.menu.home")}
                        </div>
                      </>
                    ) : (
                      <>
                        <HomeIcon width={36} height={36} color='white' />
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          {t("app.menu.home")}
                        </div>
                      </>
                    )}
                  </div>
                </Link>
                <Link
                  className='col-auto p-0'
                  onClick={() => setActiveMenuItem("invest")}
                  to='invest'
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <div className='d-flex flex-column align-items-center justify-content-start'>
                    {activeMenuItem === "invest" ? (
                      <>
                        <div
                          className='position-relative'
                          style={{
                            width: "36px",
                            height: "36px",
                            backgroundColor: "rgb(203, 108, 230)",
                            borderRadius: "50%",
                          }}
                        >
                          <div className='position-absolute top-50 start-50 translate-middle'>
                            <PlusCircle width={30} height={30} color='white' />
                          </div>
                        </div>
                        {/* <DonutIcon width={36} height={36} color='white' /> */}
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          {t("app.menu.invest")}
                        </div>
                      </>
                    ) : (
                      <>
                        <PlusCircle width={36} height={36} color='white' />
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          {t("app.menu.invest")}
                        </div>
                      </>
                    )}
                  </div>
                </Link>
                <Link
                  className='col-auto p-0'
                  onClick={() => setActiveMenuItem("portfolio")}
                  to='portfolio'
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <div className='d-flex flex-column align-items-center justify-content-start'>
                    {activeMenuItem === "portfolio" ? (
                      <>
                        <div
                          className='position-relative d-flex align-items-center justify-content-center'
                          style={{
                            width: "36px",
                            height: "36px",
                          }}
                        >
                          <div
                            className='w-100 h-100 d-flex align-items-center justify-content-center'
                            style={{
                              backgroundColor: "rgb(203, 108, 230)",
                              borderRadius: "50%",
                              // top: '0',
                              margin: "0 auto",
                              paddingBottom: "1px",
                              // transform: 'translate(-2%, -8%)'
                            }}
                          >
                            <DonutIcon width={30} height={30} color='white' />
                          </div>
                        </div>
                        {/* <DonutIcon width={36} height={36} color='white' /> */}
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          {t("app.menu.portfolio")}
                        </div>
                      </>
                    ) : (
                      <>
                        <DonutIcon width={36} height={36} color='white' />
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          {t("app.menu.portfolio")}
                        </div>
                      </>
                    )}
                  </div>
                </Link>
                {/* <Link
                  className='col-auto p-0'
                  onClick={() => setActiveMenuItem("goals")}
                  to='goals'
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <div className='d-flex flex-column align-items-center justify-content-start'>
                    {activeMenuItem === "goals" ? (
                      <>
                        <div
                          className='position-relative d-flex align-items-center justify-content-center'
                          style={{
                            width: "36px",
                            height: "36px",
                          }}
                        >
                          <div
                            className='w-100 h-100 d-flex align-items-center justify-content-center'
                            style={{
                              backgroundColor: "rgb(203, 108, 230)",
                              borderRadius: "50%",
                              // top: '0',
                              margin: "0 auto",
                              paddingBottom: "1px",
                              // transform: 'translate(-2%, -8%)'
                            }}
                          >
                            <ValidIcon width={30} height={30} color='white' />
                          </div>
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          Goals
                        </div>
                      </>
                    ) : (
                      <>
                        <ValidIcon width={36} height={36} color='white' />
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          Goals
                        </div>
                      </>
                    )}
                  </div>
                </Link> */}
                <Link
                  className='col-auto p-0'
                  onClick={() => setActiveMenuItem("learn")}
                  to='learn'
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <div className='d-flex flex-column align-items-center justify-content-start'>
                    {activeMenuItem === "learn" ? (
                      <>
                        <div
                          className='position-relative d-flex align-items-center justify-content-center'
                          style={{
                            width: "36px",
                            height: "36px",
                          }}
                        >
                          <div
                            className='w-100 h-100 d-flex align-items-center justify-content-center'
                            style={{
                              backgroundColor: "rgb(203, 108, 230)",
                              borderRadius: "50%",
                              // top: '0',
                              margin: "0 auto",
                              paddingBottom: "1px",
                              paddingRight: ".075rem",
                              // transform: 'translate(-2%, -8%)'
                            }}
                          >
                            <LearnIcon width={30} height={30} color='white' />
                          </div>
                        </div>
                        {/* <DonutIcon width={36} height={36} color='white' /> */}
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          {t("app.menu.learn")}
                        </div>
                      </>
                    ) : (
                      <>
                        <LearnIcon width={36} height={36} color='white' />
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          {t("app.menu.learn")}
                        </div>
                      </>
                    )}
                  </div>
                </Link>
                {/* <Link
                  className='col-auto p-0'
                  onClick={() => setActiveMenuItem("requests")}
                  to='requests'
                  style={{
                    textDecoration: "none",
                  }}
                >
                  <div className='d-flex flex-column align-items-center justify-content-start'>
                    {activeMenuItem === "requests" ? (
                      <>
                        <div
                          className='position-relative d-flex align-items-center justify-content-center'
                          style={{
                            width: "36px",
                            height: "36px",
                          }}
                        >
                          <div
                            className='w-100 h-100 d-flex align-items-center justify-content-center'
                            style={{
                              backgroundColor: "rgb(203, 108, 230)",
                              borderRadius: "50%",
                              // top: '0',
                              margin: "0 auto",
                              paddingBottom: "1px",
                              // transform: 'translate(-2%, -8%)'
                            }}
                          >
                            <img
                              src={RequestIcon}
                              className='ms-1'
                              style={{
                                width: "24px",
                                height: "24px",
                                color: "white",
                                filter: "invert(100%) brightness(200%)",
                                // padding: '2px'
                              }}
                            />
                          </div>
                        </div>
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          Requests
                        </div>
                      </>
                    ) : (
                      <>
                        <img
                          src={RequestIcon}
                          className='img-fluid'
                          style={{
                            width: "36px",
                            height: "36px",
                            color: "white",
                            filter: "invert(100%) brightness(200%)",
                            padding: "3px",
                          }}
                        />
                        <div
                          className=''
                          style={{
                            fontSize: "0.75rem",
                            fontWeight: "400",
                            color: "rgba(255,255,255,0.96)",
                            textAlign: "center",
                            lineHeight: "1.15",
                            letterSpacing: "0.05em",
                            marginTop: "6px",
                          }}
                        >
                          Requests
                        </div>
                      </>
                    )}
                  </div>
                </Link> */}
              </div>
            </div>

            <div className='col col-lg p-0'>
              <div
                className='d-flex align-items-center justify-content-end'
                style={{
                  gap: screenSize <= 575 ? "8px" : "12px",
                  // flex: '0 0 auto'
                }}
              >
                {/* SEARCH - DESKTOP */}
                <div
                  className='d-none d-sm-block position-relative w-100'
                  style={{
                    maxWidth: "calc(100% - 115px)",
                  }}
                >
                  <div
                    className='d-sm-flex input-group search-input-desktop position-relative'
                    style={{
                      borderRadius: "99px",
                    }}
                  >
                    <Select
                      ref={searchInputRef}
                      className='basic-single flex-fill'
                      classNamePrefix='search-input'
                      name='search-input'
                      defaultValue={null}
                      value={null}
                      inputValue={searchInput}
                      getOptionValue={(opt) => opt.value}
                      getOptionLabel={(opt) => opt.label}
                      onInputChange={(value, { action }) => {
                        if (action === "input-change") setSearchInput(value);
                      }}
                      components={{
                        Option: CustomOption,
                        DropdownIndicator: false,
                        IndicatorSeparator: false,
                      }}
                      // getOptionLabel={option }
                      isDisabled={false}
                      isLoading={loading.funds}
                      isClearable={false}
                      isRtl={false}
                      menuIsOpen={searchInput.length > 0 && searchFocus}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          setSearchFocus(false);
                          searchInputRef.current?.blur();
                        }
                      }}
                      onChange={(option, actionMeta) => {
                        if (actionMeta.action === "select-option" && option?.redirection) {
                          // console.log(option);
                          // const searchQuery = `${option.redirection}?search=${}&query=term`
                          if (option.search_type === "fund") {
                            // navigate(`${option.redirection}?search=${option.value}&query=term`);
                            // navigate(`/invest/${option.value}`);
                            setShowFundsDetail(() => ({ show: true, symbol: option.value }))
                          } else if (option.search_type === "glossary") {
                            navigate(`${option.redirection}?search=${slugify(option.value)}&query=term`);

                            setActiveMenuItem("learn");
                          } else {
                            navigate(option.redirection);
                          }
                          setSearchInput("");
                          // setActiveMenuItem(option.search_type === "fund" ? "invest" : "learn");
                          // setSearchFocus(false);
                        }
                      }}
                      isSearchable={true}
                      options={searchData.length ? searchData : []}
                      onFocus={() => setSearchFocus(true)}
                      onBlur={() => setSearchFocus(false)}
                      openMenuOnClick={false}
                      openMenuOnFocus={false}
                      placeholder={t("app.menu.search")}
                      styles={{
                        container: (base) => ({
                          ...base,
                          width: "100%",
                          maxWidth: "100%",
                          // maxWidth: 'calc(100% - 20px)'
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: "0",
                          flex: 1,
                          minWidth: 0,
                        }),
                        multiValue: (provided) => ({
                          ...provided,
                        }),
                        singleValue: (provided, state) => ({
                          ...provided,
                          width: "100%",
                          maxWidth: "100%",
                          // color: state.isFocused ? (theme === 'dark' ? 'rgb(203, 110, 209)' : '') : 'rgba(240, 240, 240, .95)',
                          // color: state.isFocused ? (theme === 'dark' ? 'rgb(203, 110, 209)' : '') : 'rgba(240, 240, 240, .95)',
                        }),
                        menu: (provided) => ({
                          ...provided,
                          zIndex: "200",
                          fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                          // backgroundColor: 'none',
                          background:
                            theme === "light"
                              ? "linear-gradient(135deg,rgb(230, 230, 240) 0%, rgb(223, 223, 235) 33%, rgb(218, 218, 232) 66%, rgb(215, 215, 227) 100%)"
                              : "linear-gradient(135deg,rgb(44, 44, 48) 0%, rgb(48, 48, 53) 33%, rgb(52, 52, 57) 66%, rgb(55, 55, 60) 100%)",
                        }),
                        control: (provided, state) => ({
                          ...provided,
                          flex: 1,
                          minWidth: 0,
                          width: "100%",
                          padding: "0 38px 0 12px",
                          minHeight: screenSize >= 576 ? "38px" : "38px",
                          fontSize: screenSize >= 576 ? "1rem" : ".95rem",
                          // color: state.isFocused ? (theme === 'dark' ? 'rgb(203, 110, 209)' : '') : 'rgba(240, 240, 240, .95)',
                          backgroundColor: state.isFocused ? (theme === "dark" ? "rgb(51, 51, 55)" : "rgba(255,255,255,.8)") : "none",
                          borderRadius: "99px",
                          border: `1px solid ${state.isFocused ? "rgb(57, 136, 233)" : "rgba(255,255,255,.3)"}`,
                          // borderColor: state.isFocused ? (theme === 'dark' ? 'rgb(203, 110, 209)' : '') : 'rgba(255,255,255,.3)',
                          boxShadow: state.isFocused ? (theme === "dark" ? "0 0 2px 2px rgba(255,255,255,.15)" : "0 0 2px 2px rgba(50, 50, 50, 0.15)") : "",
                          // color: state.isFocused ? (theme === 'dark' ? 'rgb(203, 110, 209)' : '') : 'rgba(240, 240, 240, .95)',
                          fontFamily: "inherit",
                          // boxShadow: state.isFocused
                          //   ? `0 0 2px 1px ${theme === "dark" ? "rgba(136, 136, 136, 0.5)" : "rgba(136, 136, 136, 0.5)"}` // e.g. a semi-transparent red ring
                          //   : "none",
                          "&:hover": {
                            borderColor: state.isFocused ? "" : theme === "dark" ? "rgba(255, 255, 255, .8)" : "rgb(174, 172, 175)",
                          },
                        }),
                        input: (provided, state) => ({
                          ...provided,
                          flex: 1,
                          minWidth: 0, // allow it to shrink below its contents
                          color: searchFocus ? (theme === "dark" ? "rgb(203, 110, 209)" : "") : "rgba(240, 240, 240, .95)",
                          // color: state.isFocused ? (theme === 'dark' ? "rgb(203, 110, 209)" : "rgb(203, 110, 209)") : "rgb(197, 197, 197)",
                        }),
                        placeholder: (provided, state) => ({
                          ...provided,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color:
                            theme === "light"
                              ? state.isFocused
                                ? "rgba(0, 0, 0, 0.4)"
                                : "rgba(240, 240, 240, .65)"
                              : state.isFocused
                                ? "rgba(240, 240, 240, .5)"
                                : "rgba(240, 240, 240, .65)",
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          // backgroundColor: theme === 'dark' ? "rgb(230, 230, 230)" : "rgb(70, 70, 78)",
                          borderRadius: 4,
                          padding: 0,
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          padding: "12px",
                          color: state.isSelected ? (theme === "dark" ? "rgb(250,250,250)" : "rgb(240,240,240)") : theme === "dark" ? "rgba(240, 240, 240, .95)" : "rgb(33,37,41)",
                          backgroundColor: state.isFocused ? (theme === "dark" ? "rgb(68, 68, 74)" : "rgb(205, 202, 220)") : "none",
                          // backgroundColor: state.isSelected
                          //   ? theme === "dark"
                          //     ? "rgb(203, 110, 209)"
                          //     : "rgb(44, 106, 182)"
                          //   : theme === "dark"
                          //     ? "rgb(60, 60, 67)"
                          //     : "rgb(230, 230, 230)",
                          fontWeight: "400",
                          "&:hover": {
                            cursor: "pointer",
                            backgroundColor: state.isSelected ? "" : theme === "dark" ? "rgb(68, 68, 74)" : "rgb(205, 202, 220)",
                            color: state.isSelected ? "" : theme === "dark" ? "rgb(33,37,41)" : "",
                          },
                        }),
                      }}
                    />
                    {/* <input
                      type='text'
                      className='form-control pe-0'
                      placeholder='Search Company ...'
                      aria-label='Search Company ...'
                      aria-describedby='button'
                      onFocus={() => setSearchFocus(true)}
                      onBlur={() => setSearchFocus(false)}
                      style={{
                        fontSize: "0.8rem",
                        border: "none",
                        boxShadow: "none",
                        padding: "0.375rem 0.6rem",
                        paddingLeft: "0.85rem",
                        background: searchFocus ? (theme === "dark" ? "var(--theme-dark-two)" : "var(--theme-light-two)") : "none",
                        color: theme === "dark" ? "rgba(255,255,255,0.9)" : searchFocus ? "rgb(88, 88, 88)" : "rgba(255,255,255,0.9)",
                        borderTopLeftRadius: "99px",
                        borderBottomLeftRadius: "99px",
                        // transition: 'all 0.15s ease-in-out'
                      }}
                    /> */}
                    <button
                      className='btn ps-0 pt-0 pe-0 m-0 d-flex align-items-center justify-content-center position-absolute top-50 translate-middle-y'
                      type='button'
                      id='button-addon2'
                      style={{
                        border: "none",
                        borderTopRightRadius: "99px",
                        borderBottomRightRadius: "99px",
                        right: "2px",
                        // background: searchFocus ? (theme === "dark" ? "var(--theme-dark-two)" : "var(--theme-light-two") : "none",
                        width: "38px",
                        height: "38px",
                        color: searchFocus ? (theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)") : "rgb(255,255,255)",
                        paddingBottom: "1px",
                        transition: "none",
                        // transition: 'all 0.15s ease-in-out'
                      }}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width={20}
                        height={20}
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth={2}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-search'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0' />
                        <path d='M21 21l-6 -6' />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* SEARCH - MOBILE */}
                <div id='search-mobile-wrapper' className='d-sm-none'>
                  <form action='' autoComplete='on'>
                    <input
                      id='search'
                      name='search'
                      type='text'
                      placeholder={t("app.menu.search")}
                      autoComplete='off'
                      onFocus={() => setSearchFocus(true)}
                      onBlur={() => setSearchFocus(false)}
                      style={{
                        background: searchFocus ? (theme === "dark" ? "var(--theme-dark-two)" : "rgba(240,240,240)") : "none",
                        color: theme === "dark" ? "rgb(238,238,238)" : "rgb(88, 88, 88)",
                        // top: 0,
                        right: isRTL ? "" : 0,
                        left: isRTL ? 0 : ""
                      }}
                    />

                    <div className='search_submit' type='submit' style={{
                      right: isRTL ? "" : 0,
                      left: isRTL ? 0 : ""
                    }}>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width={searchFocus ? 32 : 36}
                        height={searchFocus ? 32 : 36}
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth={searchFocus ? 1.5 : 1.25}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-search'
                        style={{
                          transition: "all 0.2s ease-in-out",
                        }}
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0' />
                        <path d='M21 21l-6 -6' />
                      </svg>
                    </div>
                  </form>
                </div>

                <div className='dropdown'>
                  <button className='btn dropdown-toggle p-0 m-0 border-0' type='button' data-bs-toggle='dropdown' aria-expanded='false'>
                    <div
                      className='d-flex align-items-center justify-content-center'
                      style={{
                        width: "36px",
                        height: "36px",
                        border: "1px solid rgba(255,255,255,.8)",
                        borderRadius: "999px",
                        backgroundColor: "rgba(255,255,255,.075)",
                        color: 'rgba(225, 243, 244, 1)'
                      }}
                    >
                      {currentLanguageConfig.flag(21, 21)}
                    </div>
                  </button>
                  <ul
                    className='dropdown-menu lang-menu'
                    id='notification_menu'
                    aria-labelledby='notification_menu'
                    style={{
                      backgroundColor: theme === "dark" ? "var(--theme-dark-two)" : "var(--theme-light-two)",
                      ["--bs-dropdown-padding-y"]: "4px",
                      ["--bs-dropdown-padding-x"]: "5px",
                      ["--bs-dropdown-min-width"]: "12rem",
                      ["--bs-dropdown-font-size"]: ".94rem",
                      ["--bs-dropdown-link-color"]: theme === "dark" ? "rgb(240,240,240)" : "rgb(33, 37, 41)",
                      ["--bs-dropdown-link-hover-bg"]: theme === "dark" ? "rgb(90, 90, 90)" : "rgb(200,200,200)",
                      ["--bs-dropdown-link-hover-color"]: theme === "dark" ? "rgb(240,240,240)" : "rgb(33, 37, 41)",
                      ["--bs-dropdown-link-active-bg"]: theme === "dark" ? "rgb(90, 90, 90)" : "rgb(200,200,200)",
                      ["--bs-dropdown-link-active-color"]: theme === "dark" ? "rgb(240,240,240)" : "rgb(33, 37, 41)",
                    }}
                  >
                    {SUPPORTED_LANGUAGES.map((item) => {
                      return (
                        <li key={item.code} style={{ cursor: 'pointer' }}>
                          <a
                            className='dropdown-item d-flex align-items-center justify-content-start flex-nowrap'
                            style={{
                              backgroundColor: currentLanguageConfig.code === item.code ? (theme === "dark" ? "rgb(90, 90, 90)" : "rgb(200,200,200)") : "",
                              gap: "9px",
                            }}
                            onClick={() => {
                              changeLanguage(item.code);
                            }}
                          >
                            <span style={{
                              color: theme === 'dark' ? 'rgb(238 145 244)' : 'rgb(63 140 233)'
                            }}>
                              {item.flag(18, 18)}
                            </span>
                            {item.nativeName}
                          </a>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* USER MENU - DROPDOWN */}
                <div className='dropdown'>
                  <div className="position-relative dropdown-toggle" role="button" data-bs-toggle='dropdown' data-bs-target='#user_menu' aria-expanded='false'>
                    {unreadNotificationsCount !== 0 ? (
                      <div className="position-absolute" style={{
                        top: '1px',
                        right: '1px'
                      }}>
                        <div className="d-flex align-items-center justify-content-center" style={{
                          borderRadius: '999px',
                          height: '16px',
                          width: '16px',
                          backgroundColor: theme === 'dark' ? 'rgb(180, 27, 71)' : '',
                          fontSize: '12px',
                          // lineHeight: '1',
                          color: theme === 'dark' ? 'rgb(255,255,255)' : ''
                        }}>
                          {unreadNotificationsCount > 99 ? '+99' : unreadNotificationsCount}
                        </div>
                      </div>
                    ) : ''}
                    <button className='btn p-0 m-0 border-0' type='button'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width={44}
                        height={44}
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth={0.6}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-user-circle'
                        style={{
                          color: "white",
                          marginLeft: "-4px",
                        }}
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0' />
                        <path d='M12 10m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0' />
                        <path d='M6.168 18.849a4 4 0 0 1 3.832 -2.849h4a4 4 0 0 1 3.834 2.855' />
                      </svg>
                    </button>
                  </div>
                  <ul
                    className='dropdown-menu user-menu'
                    id='user_menu'
                    style={{
                      backgroundColor: theme === "dark" ? "var(--theme-dark-two)" : "var(--theme-light-two)",
                      ["--bs-dropdown-link-color"]: theme === "dark" ? "rgb(240,240,240)" : "rgb(33, 37, 41)",
                      ["--bs-dropdown-link-hover-bg"]: theme === "dark" ? "rgb(90, 90, 90)" : "rgb(200,200,200)",
                      ["--bs-dropdown-link-hover-color"]: theme === "dark" ? "rgb(240,240,240)" : "rgb(33, 37, 41)",
                      ["--bs-dropdown-link-active-bg"]: theme === "dark" ? "rgb(90, 90, 90)" : "rgb(200,200,200)",
                      ["--bs-dropdown-link-active-color"]: theme === "dark" ? "rgb(240,240,240)" : "rgb(33, 37, 41)",
                    }}
                  >
                    {user ? (
                      <>
                        <li>
                          <a
                            className='dropdown-item dropdown-item-extend'
                            href='#'
                            style={{
                              color: `rgb(${theme === "dark" ? "230, 230, 230" : "75, 75, 75"})`,
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              viewBox='0 0 24 24'
                              width={18}
                              height={18}
                              fill='none'
                              stroke='currentColor'
                              strokeWidth={2}
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-user'
                              style={{
                                color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                              }}
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0' />
                              <path d='M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2' />
                            </svg>
                            {t("dropdown.user.profile")}
                          </a>
                        </li>
                        <li>
                          <Link
                            className='dropdown-item dropdown-item-extend d-flex align-items-center justify-content-start'
                            to='/notifications'
                            style={{
                              color: `rgb(${theme === "dark" ? "230, 230, 230" : "75, 75, 75"})`,
                            }}
                          >
                              <svg
                                xmlns='http://www.w3.org/2000/svg'
                                width={18}
                                height={18}
                                viewBox='0 0 24 24'
                                fill='none'
                                stroke='currentColor'
                                strokeWidth={2}
                                strokeLinecap='round'
                                strokeLinejoin='round'
                                className='icon icon-tabler icons-tabler-outline icon-tabler-bell'
                                style={{
                                  color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                                }}
                              >
                                <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                                <path d='M10 5a2 2 0 1 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6' />
                                <path d='M9 17v1a3 3 0 0 0 6 0v-1' />
                              </svg>
                              {t("dropdown.user.notify")}

                            {unreadNotificationsCount !== 0 ? (
                                <div className="d-flex align-items-center justify-content-center ms-auto" style={{
                                  borderRadius: '999px',
                                  height: '16px',
                                  width: '16px',
                                  backgroundColor: theme === 'dark' ? 'rgb(180, 27, 71)' : '',
                                  fontSize: '12px',
                                  // lineHeight: '1',
                                  color: theme === 'dark' ? 'rgb(255,255,255)' : ''
                                }}>
                                  {unreadNotificationsCount > 99 ? '+99' : unreadNotificationsCount}
                                </div>
                            ) : ''}
                          </Link>
                        </li>
                        <li>
                          <Link
                            className='dropdown-item dropdown-item-extend'
                            to='/change-password'
                            style={{
                              color: `rgb(${theme === "dark" ? "230, 230, 230" : "75, 75, 75"})`,
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width={18}
                              height={18}
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth={2}
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-tool'
                              style={{
                                color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                              }}
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M7 10h3v-3l-3.5 -3.5a6 6 0 0 1 8 8l6 6a2 2 0 0 1 -3 3l-6 -6a6 6 0 0 1 -8 -8l3.5 3.5' />
                            </svg>
                            {t("dropdown.user.change")}
                          </Link>
                        </li>
                        <li className='mt-1'>
                          <a
                            className='dropdown-item dropdown-item-extend'
                            href='#'
                            style={{
                              color: `rgb(${theme === "dark" ? "230, 230, 230" : "75, 75, 75"})`,
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width={18}
                              height={18}
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth={2}
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-settings'
                              style={{
                                color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                              }}
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z' />
                              <path d='M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0' />
                            </svg>
                            {t("dropdown.user.settings")}
                          </a>
                        </li>
                        <li className='mt-1'>
                          <button
                            className='dropdown-item dropdown-item-extend'
                            onClick={handleLogout}
                            style={{
                              color: `rgb(${theme === "dark" ? "230, 230, 230" : "75, 75, 75"})`,
                            }}
                          >
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width={18}
                              height={18}
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth={2}
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-door-enter'
                              style={{
                                color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                              }}
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M13 12v.01' />
                              <path d='M3 21h18' />
                              <path d='M5 21v-16a2 2 0 0 1 2 -2h6m4 10.5v7.5' />
                              <path d='M21 7h-7m3 -3l-3 3l3 3' />
                            </svg>
                            {t("dropdown.user.logout")}
                          </button>
                        </li>
                        <li>
                          <hr
                            className='dropdown-divider'
                            style={{
                              borderColor: `rgba(var(${theme === "dark" ? "--theme-dark-tertiary" : "--theme-light-tertiary"}), 0.175)`,
                            }}
                          />
                        </li>
                        <li>
                          <div className='d-flex align-items-center justify-content-center gap-1 flex-nowrap'>
                            <a
                              className='dropdown-item dropdown-item-btn'
                              onClick={() => setTheme(theme == "light" ? "dark" : "light")}
                              style={{ backgroundColor: theme === "light" ? "rgb(217, 217, 217)" : "rgb(229, 229, 229)" }}
                            >
                              {theme == "light" ? <MoonIcon width={22} height={22} /> : <SunIcon width={22} height={22} />}
                            </a>
                            {/* <a className="dropdown-item dropdown-item-btn" onClick={() => setTheme('dark')}>
															<svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="currentColor" className="icon icon-tabler icons-tabler-filled icon-tabler-moon"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 1.992a10 10 0 1 0 9.236 13.838c.341 -.82 -.476 -1.644 -1.298 -1.31a6.5 6.5 0 0 1 -6.864 -10.787l.077 -.08c.551 -.63 .113 -1.653 -.758 -1.653h-.266l-.068 -.006l-.06 -.002z" /></svg>
														</a>
														<a className="dropdown-item dropdown-item-btn" href="#">
															<svg xmlns="http://www.w3.org/2000/svg" width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-device-laptop"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M3 19l18 0" /><path d="M5 6m0 1a1 1 0 0 1 1 -1h12a1 1 0 0 1 1 1v8a1 1 0 0 1 -1 1h-12a1 1 0 0 1 -1 -1z" /></svg>
														</a> */}
                          </div>
                        </li>
                      </>
                    ) : (
                      <>
                        <li className='mt-1'>
                          <Link className='dropdown-item dropdown-item-extend' to='/auth/login'>
                            <svg
                              xmlns='http://www.w3.org/2000/svg'
                              width={18}
                              height={18}
                              viewBox='0 0 24 24'
                              fill='none'
                              stroke='currentColor'
                              strokeWidth={1.5}
                              strokeLinecap='round'
                              strokeLinejoin='round'
                              className='icon icon-tabler icons-tabler-outline icon-tabler-user ms-1'
                              style={{
                                color: theme === "dark" ? "var(--theme-light-secondary)" : "var(--theme-light-primary)",
                              }}
                            >
                              <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                              <path d='M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0' />
                              <path d='M6 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2' />
                            </svg>
                            {t("dropdown.user.login")}
                          </Link>
                        </li>
                        <li>
                          <hr
                            className='dropdown-divider'
                            style={{
                              borderColor: `rgba(var(${theme === "dark" ? "--theme-dark-tertiary" : "--theme-light-tertiary"}), 0.175)`,
                            }}
                          />
                        </li>
                        <li>
                          <div className='d-flex align-items-center justify-content-center gap-1 flex-nowrap'>
                            <a
                              className='dropdown-item dropdown-item-btn'
                              onClick={() => setTheme(theme == "light" ? "dark" : "light")}
                              style={
                                {
                                  // backgroundColor: theme === "light" ? "rgb(217, 217, 217)" : "rgb(229, 229, 229)"
                                }
                              }
                            >
                              {theme == "light" ? <MoonIcon width={22} height={22} /> : <SunIcon width={22} height={22} />}
                            </a>
                          </div>
                        </li>
                      </>
                    )}
                  </ul>
                </div>

                {/* <svg
									xmlns="http://www.w3.org/2000/svg"
									width={42}
									height={42}
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth={1}
									strokeLinecap="round"
									strokeLinejoin="round"
									className="icon icon-tabler icons-tabler-outline icon-tabler-user-circle"
									style={{
										color: 'white'
									}}
								>
									<path stroke="none" d="M0 0h24v24H0z" fill="none" />
									<path d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0" />
									<path d="M12 10m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
									<path d="M6.168 18.849a4 4 0 0 1 3.832 -2.849h4a4 4 0 0 1 3.834 2.855" />
								</svg> */}
              </div>
            </div>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div
          className='position-relative col-12 p-0 flex-grow-1 order-1 order-sm-2'
          style={{
            // backgroundColor: theme === "dark" ? "var(--theme-dark-one)" : "var(--theme-light-one)",
            // backdropFilter: 'saturate(0.5)',
            borderRadius: "20px",
          }}
        >
          {tabsData[activeMenuItem]?.length ? (
            <div
              className='position-relative'
              style={{
                overflow: "hidden",
                borderTopLeftRadius: "20px",
                borderTopRightRadius: "20px",
              }}
            >
              {tabScrollingLeft ? (
                <button
                  className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                  onClick={() => handleLeftScroll()}
                  style={{
                    top: "7px",
                    left: "3px",
                    color: "var(--theme-light-secondary)",
                    padding: "2px",
                    background: theme === "dark" ? "rgb(46, 51, 58)" : "rgb(217, 230, 248)",
                    boxShadow:
                      theme === "dark"
                        ? "rgb(46, 51, 58) 0px 0px 12px 14px, rgb(46, 51, 58) -13px 0px 4px 8px"
                        : "rgb(217, 230, 248) 0px 0px 12px 14px, rgb(217, 230, 248) -12px 0px 0px 6px",
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width={30}
                    height={30}
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth={2}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-left'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M15 6l-6 6l6 6' />
                  </svg>
                </button>
              ) : (
                ""
              )}

              {tabScrollingRight ? (
                <button
                  className='btn border-0 m-0 position-absolute d-flex align-items-center justify-content-center'
                  onClick={() => handleRightScroll()}
                  style={{
                    top: "7px",
                    right: "3px",
                    color: "var(--theme-light-secondary)",
                    padding: "2px",
                    background: theme === "dark" ? "rgb(58, 51, 59)" : "rgb(243, 226, 246)",
                    boxShadow:
                      theme === "dark"
                        ? "rgb(58, 51, 59) 0px 0px 12px 14px, rgb(58, 51, 59) 13px 0px 4px 8px"
                        : "rgb(243, 226, 246) 0px 0px 12px 14px, rgb(243, 226, 246) 13px 0px 4px 8px",
                  }}
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    width={30}
                    height={30}
                    viewBox='0 0 24 24'
                    fill='none'
                    stroke='currentColor'
                    strokeWidth={2}
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    className='icon icon-tabler icons-tabler-outline icon-tabler-chevron-right'
                  >
                    <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                    <path d='M9 6l6 6l-6 6' />
                  </svg>
                </button>
              ) : (
                ""
              )}

              <div
                className='tabs-row row w-100 mx-auto align-items-center justify-content-sm-center p-2 gap-2 flex-nowrap overflow-x-auto mw-100'
                ref={tabsScrollRef}
                style={{
                  scrollbarWidth: "none",
                  scrollBehavior: "smooth",
                  borderTopLeftRadius: "20px",
                  borderTopRightRadius: "20px",
                }}
              >
                {tabsData[activeMenuItem].map((tabItem, index) => {
                  const numberOfTabs = tabsData[activeMenuItem].length;
                  const isPortfolio = activeMenuItem === "portfolio";

                  // if active menu is portfolio, we need to adjust this because of the addPortfolio modal button that was added to home_layout
                  let appliedMargin;

                  if (numberOfTabs === 1 && !isPortfolio) {
                    appliedMargin = "mx-auto";
                  } else if (isPortfolio) {
                    appliedMargin = "ms-auto";
                  } else {
                    if (index === 0) {
                      appliedMargin = "ms-auto";
                    } else if (index === numberOfTabs - 1) {
                      appliedMargin = "me-auto";
                    }
                  }

                  return (
                    // <div className={`col-auto p-0 ${numberOfTabs === 1  ? "mx-auto" : (index === 0 ? 'ms-auto' : (index === numberOfTabs - 1) ? 'me-auto' : '')}`} key={index}>
                    <div className={`col-auto p-0 ${appliedMargin}`} key={index}>
                      {/* <Link className={`btn btn-primary border-0 ${currentActiveTab() === tabItem.path ? 'active' : ''}`} to={tabItem.path.indexOf('/') == -1 ? '/' : tabItem.path} type="button" style={{ */}
                      <Link
                        className={`btn btn-primary border-0 ${currentActiveTab() === tabItem.path ? "active" : ""}`}
                        to={tabItem.path}
                        type='button'
                        style={{
                          lineHeight: "1",
                          fontSize: "0.8rem",
                          padding: "10px 16px 11px",
                          minWidth: "82px",
                          borderRadius: "99px",
                        }}
                      >
                        {tabItem.name}
                      </Link>
                    </div>
                  );
                })}
                {/* {activeMenuItem === 'goals' ? (
									<div className='col-auto p-0'>
										<button
											className="btn btn-primary border-0 gap-1 d-flex align-items-center justify-content-center"
											type="button"
											data-bs-toggle="modal"
											data-bs-target="#goalModal"
											style={{
												lineHeight: '1',
												fontSize: '0.82rem',
												padding: '7px 16px 7.5px 9px',
												// minWidth: '82px',
												borderRadius: '99px',
											}}
										>
											<svg xmlns="http://www.w3.org/2000/svg" width={19} height={19} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="icon icon-tabler icons-tabler-outline icon-tabler-plus"><path stroke="none" d="M0 0h24v24H0z" fill="none" /><path d="M12 5l0 14" /><path d="M5 12l14 0" /></svg>

											Create Goal
										</button>
									</div>
								) : ''} */}
                {activeMenuItem === "portfolio" ? (
                  <div className={`col-auto p-0 ${activeMenuItem === "portfolio" && "me-auto"}`}>
                    <button
                      className='btn btn-primary border-0'
                      type='button'
                      // data-bs-toggle="modal" data-bs-target="#watchList"
                      // onClick={() => {
                      // 	const modal = new Modal(modalRef.current);
                      // 	modal.show();
                      // }}
                      onClick={handlePortfolioModalToggle}
                      style={{
                        lineHeight: "1",
                        fontSize: "0.8rem",
                        padding: "3.5px 4px 4.5px",
                        // minWidth: '82px',
                        borderRadius: "99px",
                      }}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width={26}
                        height={26}
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                        strokeWidth={2}
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        className='icon icon-tabler icons-tabler-outline icon-tabler-plus'
                      >
                        <path stroke='none' d='M0 0h24v24H0z' fill='none' />
                        <path d='M12 5l0 14' />
                        <path d='M5 12l14 0' />
                      </svg>
                    </button>
                  </div>
                ) : (
                  ""
                )}
                {activeMenuItem === "invest" && screenSize >= 768 ? (
                  <div
                    className='col-12 col-md-auto p-0 position-absolute'
                    style={{
                      top: ".45rem",
                      right: ".5rem",
                    }}
                  >
                    <div className='position-relative'>
                      <div
                        className='position-absolute d-flex flex-column justify-content-center h-100'
                        style={{
                          color: "rgb(107 165 233)",
                          fontSize: "0.9rem",
                          paddingInline: "0.75rem",
                          paddingLeft: "1rem",
                        }}
                      >
                        Rs
                      </div>
                      <input
                        type='text'
                        className='form-control goal-form-control rounded-pill text-center'
                        placeholder='Enter Amount'
                        value={investInput && numberWithCommas(investInput)}
                        id='invest-amount'
                        onChange={(e) => {
                          let formatted = "";

                          for (const x of e.target.value) {
                            if ((x >= "0" && x <= "9") || x == ",") {
                              formatted += x;
                            }
                          }

                          setInvestInput(formatted.split(",").join(""));
                        }}
                        style={{
                          backgroundColor: "rgba(245, 250, 255, .9)",
                          fontSize: "1rem",
                          width: "200px",
                          maxWidth: "100%",
                          borderColor: "rgb(178 207 236)",
                          letterSpacing: ".03em",
                          paddingInlineStart: "2.5rem",
                          paddingInlineEnd: "1.75rem",
                          paddingBlock: ".3rem",
                          paddingBlockEnd: ".4rem",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  ""
                )}
              </div>

              {activeMenuItem === "invest" && screenSize <= 767 ? (
                <div className='p-0 pb-1 px-2 px-sm-3 text-center' style={{}}>
                  <div className='position-relative d-sm-inline-block ms-auto'>
                    <div
                      className='position-absolute d-flex flex-column justify-content-center h-100'
                      style={{
                        color: "rgb(107 165 233)",
                        fontSize: "0.85rem",
                        paddingInline: "0.75rem",
                        paddingLeft: "1rem",
                      }}
                    >
                      Rs
                    </div>
                    <input
                      type='text'
                      className='form-control goal-form-control rounded-pill text-center'
                      placeholder='Enter Amount'
                      value={investInput}
                      onChange={(e) => setInvestInput(e.target.value)}
                      id='invest-amount'
                      style={{
                        backgroundColor: "rgba(245, 250, 255, .9)",
                        fontSize: ".9rem",
                        width: screenSize >= 576 ? "200px" : "100%",
                        maxWidth: "100%",
                        borderColor: "rgb(178 207 236)",
                        paddingInlineStart: "2.5rem",
                        paddingInlineEnd: "1.75rem",
                        paddingBlock: ".25rem",
                        paddingBlockEnd: ".35rem",
                      }}
                    />
                  </div>
                </div>
              ) : (
                ""
              )}
            </div>
          ) : (
            ""
          )}
          {/* <CustomSearchBar /> */}
          <NotificationContext.Provider value={notificationContextData}>
            <Outlet context={{
              investInput,
              setActiveMenuItem,
              showDetails: showFundsDetail,
              setShowDetails: setShowFundsDetail,
              funds: fundsData,
              loadingFunds: loading.funds,
            }} />
          </NotificationContext.Provider>
        </div>

        {/* NAVIGATION MENU - TAB */}
        <div className='col-12 p-0 flex-grow-0 flex-shrink-0 order-2 order-sm-1 d-lg-none'>
          <div
            className='row w-100 mx-auto justify-content-between justify-content-sm-center px-2 px-md-4'
            style={{
              paddingBottom: screenSize <= 575 ? "0" : "4px",
              paddingTop: "6px",
              maxWidth: screenSize <= 575 ? "280px" : "",
              gap: screenSize <= 575 ? "" : "2.25rem",
            }}
          >
            <Link
              className='col-auto p-0'
              onClick={() => setActiveMenuItem("dashboard")}
              to='dashboard'
              style={{
                textDecoration: "none",
              }}
            >
              <div className='d-flex flex-column align-items-center justify-content-start'>
                {activeMenuItem === "dashboard" ? (
                  <div
                    className='position-relative'
                    style={{
                      width: screenSize >= 576 ? "36px" : "32px",
                      height: screenSize >= 576 ? "36px" : "32px",
                    }}
                  >
                    <div
                      className='position-absolute'
                      style={{
                        backgroundColor: "rgb(203, 108, 230)",
                        zIndex: 100,
                        borderRadius: "50%",
                        top: "0",
                        margin: "0 auto",
                        transform: screenSize >= 576 ? (isRTL ? "translate(16%, -26%)" : "translate(-13%, -26%)") : (isRTL ? "translate(22%, -46%)" : "translate(-21%, -46%)"),
                        padding: activeMenuItem === "dashboard" ? "7px" : "5px",
                      }}
                    >
                      <HomeIcon width={screenSize >= 576 ? 38 : 42} height={screenSize >= 576 ? 38 : 42} color='white' />
                    </div>
                  </div>
                ) : (
                  <HomeIcon width={screenSize >= 576 ? 36 : 32} height={screenSize >= 576 ? 36 : 32} color='white' />
                )}
                <div
                  className=''
                  style={{
                    fontSize: screenSize >= 576 ? "0.75rem" : ".7rem",
                    fontWeight: "400",
                    color: "rgba(255,255,255,0.96)",
                    textAlign: "center",
                    lineHeight: "1.15",
                    letterSpacing: "0.05em",
                    marginTop: "6px",
                  }}
                >
                  {t("app.menu.home")}
                </div>
              </div>
            </Link>
            <Link
              className='col-auto p-0'
              onClick={() => setActiveMenuItem("invest")}
              to='invest'
              style={{
                textDecoration: "none",
              }}
            >
              <div className='d-flex flex-column align-items-center justify-content-start'>
                {activeMenuItem === "invest" ? (
                  <div
                    className='position-relative'
                    style={{
                      width: screenSize >= 576 ? "36px" : "32px",
                      height: screenSize >= 576 ? "36px" : "32px",
                    }}
                  >
                    <div
                      className='position-absolute'
                      style={{
                        backgroundColor: "rgb(203, 108, 230)",
                        borderRadius: "50%",
                        zIndex: 100,
                        top: "0",
                        margin: "0 auto",
                        transform: screenSize >= 576 ? (isRTL ? "translate(16%, -26%)" : "translate(-13%, -26%)") : (isRTL ? "translate(22%, -46%)" : "translate(-21%, -46%)"),
                        padding: activeMenuItem === "invest" ? "7px" : "5px",
                      }}
                    >
                      <PlusCircle width={screenSize >= 576 ? 38 : 42} height={screenSize >= 576 ? 38 : 42} color='white' />
                    </div>
                  </div>
                ) : (
                  <PlusCircle width={screenSize >= 576 ? 36 : 32} height={screenSize >= 576 ? 36 : 32} color='white' />
                )}
                <div
                  className=''
                  style={{
                    fontSize: screenSize >= 576 ? "0.75rem" : ".7rem",
                    fontWeight: "400",
                    color: "rgba(255,255,255,0.96)",
                    textAlign: "center",
                    lineHeight: "1.15",
                    letterSpacing: "0.05em",
                    marginTop: "6px",
                  }}
                >
                  {t("app.menu.invest")}
                </div>
              </div>
            </Link>
            <Link
              className='col-auto p-0'
              onClick={() => setActiveMenuItem("portfolio")}
              to='portfolio'
              style={{
                textDecoration: "none",
              }}
            >
              <div className='d-flex flex-column align-items-center justify-content-start'>
                {activeMenuItem === "portfolio" ? (
                  <div
                    className='position-relative'
                    style={{
                      width: screenSize >= 576 ? "36px" : "32px",
                      height: screenSize >= 576 ? "36px" : "32px",
                    }}
                  >
                    <div
                      className='position-absolute'
                      style={{
                        backgroundColor: "rgb(203, 108, 230)",
                        borderRadius: "50%",
                        zIndex: 100,
                        top: "0",
                        margin: "0 auto",
                        transform: screenSize >= 576 ? (isRTL ? "translate(16%, -26%)" : "translate(-13%, -26%)") : (isRTL ? "translate(22%, -46%)" : "translate(-21%, -46%)"),
                        padding: activeMenuItem === "portfolio" ? "7px" : "5px",
                      }}
                    >
                      <DonutIcon width={screenSize >= 576 ? 38 : 42} height={screenSize >= 576 ? 38 : 42} color='white' />
                    </div>
                  </div>
                ) : (
                  <DonutIcon width={screenSize >= 576 ? 36 : 32} height={screenSize >= 576 ? 36 : 32} color='white' />
                )}
                <div
                  className=''
                  style={{
                    fontSize: screenSize >= 576 ? "0.75rem" : ".7rem",
                    fontWeight: "400",
                    color: "rgba(255,255,255,0.96)",
                    textAlign: "center",
                    lineHeight: "1.15",
                    letterSpacing: "0.05em",
                    marginTop: "6px",
                  }}
                >
                  {t("app.menu.portfolio")}
                </div>
              </div>
            </Link>
            {/* <Link
              className='col-auto p-0'
              onClick={() => setActiveMenuItem("goals")}
              to='goals'
              style={{
                textDecoration: "none",
              }}
            >
              <div className='d-flex flex-column align-items-center justify-content-start'>
                {activeMenuItem === "goals" ? (
                  <div
                    className='position-relative'
                    style={{
                      width: screenSize >= 576 ? "36px" : "32px",
                      height: screenSize >= 576 ? "36px" : "32px",
                    }}
                  >
                    <div
                      className='position-absolute'
                      style={{
                        backgroundColor: "rgb(203, 108, 230)",
                        borderRadius: "50%",
                        zIndex: 100,
                        top: "0",
                        margin: "0 auto",
                        transform: screenSize >= 576 ? "translate(-13%, -26%)" : "translate(-21%, -46%)",
                        padding: activeMenuItem === "goals" ? "7px" : "5px",
                      }}
                    >
                      <ValidIcon width={screenSize >= 576 ? 38 : 42} height={screenSize >= 576 ? 38 : 42} color='white' />
                    </div>
                  </div>
                ) : (
                  <ValidIcon width={screenSize >= 576 ? 36 : 32} height={screenSize >= 576 ? 36 : 32} color='white' />
                )}
                <div
                  className=''
                  style={{
                    fontSize: screenSize >= 576 ? "0.75rem" : ".7rem",
                    fontWeight: "400",
                    color: "rgba(255,255,255,0.96)",
                    textAlign: "center",
                    lineHeight: "1.15",
                    letterSpacing: "0.05em",
                    marginTop: "6px",
                  }}
                >
                  Goals
                </div>
              </div>
            </Link> */}
            <Link
              className='col-auto p-0'
              onClick={() => setActiveMenuItem("learn")}
              to='learn'
              style={{
                textDecoration: "none",
              }}
            >
              <div className='d-flex flex-column align-items-center justify-content-start'>
                {activeMenuItem === "learn" ? (
                  <div
                    className='position-relative'
                    style={{
                      width: screenSize >= 576 ? "36px" : "32px",
                      height: screenSize >= 576 ? "36px" : "32px",
                    }}
                  >
                    <div
                      className='position-absolute'
                      style={{
                        backgroundColor: "rgb(203, 108, 230)",
                        borderRadius: "50%",
                        zIndex: 100,
                        top: "0",
                        margin: "0 auto",
                        transform: screenSize >= 576 ? (isRTL ? "translate(16%, -26%)" : "translate(-13%, -26%)") : (isRTL ? "translate(22%, -46%)" : "translate(-21%, -46%)"),
                        padding: activeMenuItem === "learn" ? "7px" : "5px",
                      }}
                    >
                      <LearnIcon width={screenSize >= 576 ? 38 : 42} height={screenSize >= 576 ? 38 : 42} color='white' />
                    </div>
                  </div>
                ) : (
                  <LearnIcon width={screenSize >= 576 ? 36 : 32} height={screenSize >= 576 ? 36 : 32} color='white' />
                )}
                <div
                  className=''
                  style={{
                    fontSize: screenSize >= 576 ? "0.75rem" : ".7rem",
                    fontWeight: "400",
                    color: "rgba(255,255,255,0.96)",
                    textAlign: "center",
                    lineHeight: "1.15",
                    letterSpacing: "0.05em",
                    marginTop: "6px",
                  }}
                >
                  {t("app.menu.learn")}
                </div>
              </div>
            </Link>
            {/* <Link
              className='col-auto p-0'
              onClick={() => setActiveMenuItem("requests")}
              to='requests'
              style={{
                textDecoration: "none",
              }}
            >
              <div className='d-flex flex-column align-items-center justify-content-start'>
                {activeMenuItem === "requests" ? (
                  <div
                    className='position-relative'
                    style={{
                      width: screenSize >= 576 ? "36px" : "32px",
                      height: screenSize >= 576 ? "36px" : "32px",
                    }}
                  >
                    <div
                      className='position-absolute'
                      style={{
                        backgroundColor: "rgb(203, 108, 230)",
                        borderRadius: "50%",
                        zIndex: 100,
                        top: "0",
                        margin: "0 auto",
                        transform: screenSize >= 576 ? "translate(-13%, -26%)" : "translate(-21%, -46%)",
                        padding: activeMenuItem === "requests" ? "7px" : "5px",
                      }}
                    >
                      <img
                        src={RequestIcon}
                        className=''
                        style={{
                          width: screenSize >= 576 ? "32px" : "44px",
                          height: screenSize >= 576 ? "32px" : "44px",
                          color: "white",
                          filter: "invert(100%) brightness(200%)",
                          padding: screenSize >= 576 ? "3px 0px 3px 5px" : "5px 0px 5px 7px",
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <img
                    src={RequestIcon}
                    className=''
                    style={{
                      width: screenSize >= 576 ? "36px" : "32px",
                      height: screenSize >= 576 ? "36px" : "32px",
                      color: "white",
                      filter: "invert(100%) brightness(200%)",
                      padding: "2px",
                    }}
                  />
                )}
                <div
                  className=''
                  style={{
                    fontSize: screenSize >= 576 ? "0.75rem" : ".7rem",
                    fontWeight: "400",
                    color: "rgba(255,255,255,0.96)",
                    textAlign: "center",
                    lineHeight: "1.15",
                    letterSpacing: "0.05em",
                    marginTop: "6px",
                  }}
                >
                  Requests
                </div>
              </div>
            </Link> */}
          </div>
        </div>

        {/* FOOTER */}
        {screenSize >= 576 ? (
          <div className='col-12 p-0 order-5'>
            <div
              className='w-100 text-center'
              style={{
                color: "rgba(255,255,255,0.9)",
                letterSpacing: "0.05em",
                fontSize: "0.85rem",
                fontWeight: "300",
              }}
            >
              {t("app.footer.a") + " "} <span style={{ fontWeight: "400" }}>{t("app.footer.b")}</span>
            </div>
          </div>
        ) : (
          ""
        )}

        {showFundsDetail.show ? (
            <FundDetailsModal
              show={showFundsDetail?.show}
              symbol={showFundsDetail?.symbol}
              loading={loading.funds}
              funds={fundsData}
              unitholder={unitholderData}
              setMenu={setActiveMenuItem}
              backdrop={true}
              keyboard={true}
              onClose={() => setShowFundsDetail(() => ({ show: false, symbol: "" }))}
            />
        ) : ""}

      </div>
    </div>
  );
};

export default HomeLayout;
